import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  Contract,
  ContractListItem,
  ContractSearch,
  EditContract,
} from '@app/features/contracts/contract.model';
import { nameContractDownloads } from '@app/features/contracts/contract-file-name';
import { DocumentTagLabelPipe } from '@app/features/documents/document-tag-label-pipe';
import { CdTDocument, DocumentTagAndId } from '@app/features/documents/document.model';
import { buildQueryParams } from '@app/shared/http.query-params';
import { SpringPageResponseData } from '@app/shared/searchable.model';
import { RUNTIME_CONFIG } from '@environments/runtime-config.token';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { DocumentsMetadata } from './../documents/document-metadata.model';
import { EmbaucheDialog } from './embauche-dialog/embauche-dialog';

@Injectable({
  providedIn: 'root',
})
export class ContractService {
  private httpClient = inject(HttpClient);

  private url = `${inject(RUNTIME_CONFIG).apiUrl}/contracts`;
  private documentTagLabelPipe = new DocumentTagLabelPipe();
  private locale = inject(LOCALE_ID);
  private readonly dialog = inject(MatDialog);

  private documentDatePipe = new DatePipe(this.locale);
  private createdSubject = new Subject<Contract>();
  private updatedSubject = new Subject<Contract>();
  private deletedSubject = new Subject<string>();

  get created$(): Observable<Contract> {
    return this.createdSubject.asObservable();
  }

  get updated$(): Observable<Contract> {
    return this.updatedSubject.asObservable();
  }

  get deleted$(): Observable<string> {
    return this.deletedSubject.asObservable();
  }

  addDocument(contractId: string, tagAndId: DocumentTagAndId): Observable<Contract> {
    return this.httpClient.post<Contract>(`${this.url}/${contractId}/document`, tagAndId);
  }

  /**
   * Supprime un contrat. La suppression est logique ou définitive selon que le contrat a déjà
   * fait l'objet de demandes de validation — l'arbitrage est fait côté API.
   * @param comment motif de suppression, obligatoire
   */
  delete(contractId: string, comment: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.url}/${contractId}`, { body: { comment } }).pipe(
      map(() => {
        this.deletedSubject.next(contractId);
      }),
    );
  }

  create(contract: EditContract): Observable<Contract> {
    return this.httpClient.post<Contract>(this.url, contract).pipe(
      map((createdContract) => {
        this.createdSubject.next(createdContract);
        return createdContract;
      }),
    );
  }

  findOne(contractId: string): Observable<Contract> {
    return this.httpClient.get<Contract>(`${this.url}/${contractId}`).pipe(
      map((contract) => {
        this.updatedSubject.next(contract);
        if (!contract || !contract.documents || !contract.documents.length) return contract;

        // group documents by tag
        const groups = new Map<string, CdTDocument[]>();
        for (const doc of contract.documents) {
          const list = groups.get(doc.tag) || [];
          list.push(doc);
          groups.set(doc.tag, list);
        }

        // For each group, sort by createdAt asc and assign an index-based name
        for (const [, list] of groups.entries()) {
          list.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          for (let i = 0; i < list.length; i++) {
            const d = list[i];
            const lastname = contract.employee.lastname.toUpperCase();
            const firstname = contract.employee.firstname;
            d.name = `${lastname} ${firstname} - ${this.documentTagLabelPipe.transform(d.tag)}`;
            if (list.length > 1) {
              d.name += ` - ${i + 1}`;
            }
            d.name += ` - ${this.documentDatePipe.transform(d.createdAt, 'yyyy-MM-dd')}.${d.mimeType.split('/').pop()}`;
          }
        }
        // Les pièces de contrat gardent ce nom à l'écran, mais se téléchargent sous celui du
        // disque réseau (RG-contrat-nom-de-telechargement).
        nameContractDownloads(contract.documents, contract.contract);

        return contract;
      }),
    );
  }

  getPdf(contractId: string): Observable<Blob> {
    return this.httpClient.get(`${this.url}/${contractId}/pdf`, { responseType: 'blob' });
  }

  getPdfName(firstname: string, lastname: string, createdDate: string): string {
    const formattedDate = new DatePipe('fr-FR').transform(createdDate, 'yyyy-MM-dd') ?? createdDate;
    return `${lastname} ${firstname} - ${this.documentTagLabelPipe.transform('contract')} - ${formattedDate}.pdf`.trim();
  }

  list(contractSearch: ContractSearch): Observable<SpringPageResponseData<ContractListItem>> {
    return this.httpClient.get<SpringPageResponseData<ContractListItem>>(this.url, {
      params: buildQueryParams(contractSearch),
    });
  }

  listSeries(firstContractId: string): Observable<ContractListItem[]> {
    return this.httpClient.get<ContractListItem[]>(`${this.url}/${firstContractId}/serie`);
  }

  open(contractId: string): void {
    this.findOne(contractId).subscribe((contract) => {
      this.dialog.open<EmbaucheDialog, Contract, Contract>(EmbaucheDialog, {
        data: contract,
        width: 'auto',
        height: 'auto',
        maxWidth: '1024px',
        disableClose: true,
        exitAnimationDuration: '600ms',
      });
    });
  }

  update(contract: EditContract): Observable<Contract> {
    return this.httpClient.put<Contract>(`${this.url}/${contract.id}`, contract).pipe(
      map((updatedContract) => {
        this.updatedSubject.next(updatedContract);
        return updatedContract;
      }),
    );
  }

  updateDocumentsMetadata(contractId: string, documentsMetadata: DocumentsMetadata) {
    return this.httpClient
      .put<Contract>(`${this.url}/${contractId}/documents-metadata`, documentsMetadata)
      .pipe(
        map((updatedContract) => {
          this.updatedSubject.next(updatedContract);
          return updatedContract;
        }),
      );
  }

  updateSocialSecurityNumber(
    contractId: string,
    socialSecurityNumber: string,
  ): Observable<Contract> {
    return this.httpClient
      .put<Contract>(`${this.url}/${contractId}/social-security-number`, { socialSecurityNumber })
      .pipe(
        map((updatedContract) => {
          this.updatedSubject.next(updatedContract);
          return updatedContract;
        }),
      );
  }

  mostRecentsByEmployee(
    employeeId: string,
    excludedContractId: string | null | undefined,
  ): Observable<ContractListItem[]> {
    return this.httpClient.post<ContractListItem[]>(
      `${this.url}/employees/${employeeId}/most-recents`,
      { excludedContractId },
      {
        params: buildQueryParams({ employeeId }),
      },
    );
  }
}
